/**
 * /student/progress — "am I getting better, and what should I work on?"
 *
 * The rest of the student side only ever shows one paper at a time, so this is
 * the only screen that can answer that question. It is written for a student,
 * not a marker: the order is what to DO first (the revision list), then the
 * evidence behind it (the score line, the class comparison, the clock), and
 * every figure is paired with the sample it came from so no single bad question
 * reads as a verdict.
 *
 * Two rules the whole page is built around:
 *   - Retakes are not progress. A second sitting of the same paper is drawn
 *     differently everywhere it appears, so a better mark on a repeat can never
 *     be mistaken for improvement over time.
 *   - We refuse to imply a trend the data cannot support. With one or two
 *     sittings the page says so in words instead of drawing a confident line.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Clock,
  Sparkles,
  Target,
} from "lucide-react";
import {
  COHORT_MEAN_MIN,
  TOPIC_ADVICE_MIN,
  type MyAttemptRow,
  type MyProgressResponse,
  type MyTopicRow,
} from "@exam/shared";
import { AppShell } from "@/components/AppShell";
import { Badge, Card, CardBody, CardHeader } from "@/components/ui";
import { cn } from "@/lib/cn";
import {
  buildChart,
  CHART,
  fetchMyProgress,
  formatDate,
  formatDateTime,
  formatDuration,
  isTooThinToAct,
  percentText,
  sortTopics,
  timeUse,
  trendCaption,
  type ProgressPoint,
} from "./data/progress";

export function MyProgressPage() {
  const [data, setData] = useState<MyProgressResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchMyProgress()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "Could not load your progress.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppShell title="My progress">
      <div className="mx-auto flex max-w-4xl flex-col gap-4">
        <Link
          to="/student"
          className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="size-3.5" /> My exams
        </Link>

        <div>
          <h1 className="text-xl font-semibold text-ink">My progress</h1>
          <p className="mt-0.5 text-xs text-ink-muted">
            Everything you have sat so far, and what it suggests you work on
            next.
          </p>
        </div>

        {loading ? (
          <Card>
            <CardBody>
              <p className="text-xs text-ink-faint">Loading your progress…</p>
            </CardBody>
          </Card>
        ) : error ? (
          <Card>
            <CardBody>
              <p className="text-xs text-violation">{error}</p>
              <p className="mt-1 text-2xs text-ink-faint">
                Your progress is built from submitted papers, so it may take a
                moment to appear after a submission.
              </p>
            </CardBody>
          </Card>
        ) : data ? (
          data.attempts.length === 0 ? (
            <EmptyState />
          ) : (
            <Progress data={data} />
          )
        ) : null}
      </div>
    </AppShell>
  );
}

function Progress({ data }: { data: MyProgressResponse }) {
  return (
    <>
      <Headline
        examsSat={data.exams_sat}
        meanPercent={data.mean_percent}
        bestPercent={data.best_percent}
      />
      {/* The revision list first: it is the only block on the page a student
          can act on this evening. Everything below it is the evidence. */}
      <ReviseBlock revise={data.revise} hasTopics={data.topics.length > 0} />
      <ScoreOverTime attempts={data.attempts} />
      <AllTopics topics={data.topics} />
      <TimeManagement attempts={data.attempts} />
      <AttemptList attempts={data.attempts} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Empty state                                                         */
/* ------------------------------------------------------------------ */

function EmptyState() {
  return (
    <Card>
      <CardBody className="flex flex-col items-start gap-3 py-8">
        <Sparkles className="size-6 text-accent" />
        <div>
          <p className="text-sm font-semibold text-ink">
            Nothing to show yet — you have not sat a paper.
          </p>
          <p className="mt-1 max-w-prose text-xs text-ink-muted">
            Once you submit your first exam this page fills in on its own: your
            score against the class, how your marks move over time, where the
            clock goes, and a short list of the topics worth revising. Nothing
            to set up.
          </p>
        </div>
        <Link
          to="/student"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:underline"
        >
          See my exams <ArrowRight className="size-3.5" />
        </Link>
      </CardBody>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Headline stats                                                      */
/* ------------------------------------------------------------------ */

function Headline({
  examsSat,
  meanPercent,
  bestPercent,
}: {
  examsSat: number;
  meanPercent: number | null;
  bestPercent: number | null;
}) {
  return (
    <Card>
      <CardBody className="grid gap-3 sm:grid-cols-3">
        <BigStat label="Exams sat" value={String(examsSat)} />
        {/* A null mean/best is "nothing yet", never 0% — a student who has not
            been marked has not scored zero. */}
        <BigStat
          label="Average"
          value={percentText(meanPercent)}
          hint={meanPercent === null ? "Nothing marked yet" : "Across every paper"}
        />
        <BigStat
          label="Best"
          value={percentText(bestPercent)}
          hint={bestPercent === null ? "Nothing marked yet" : "Your strongest paper"}
        />
      </CardBody>
    </Card>
  );
}

function BigStat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-md border border-border bg-surface-sunken px-3 py-2.5">
      <p className="text-2xs uppercase tracking-wide text-ink-faint">{label}</p>
      <p className="tabular mt-0.5 text-2xl font-semibold text-ink">{value}</p>
      {hint ? <p className="text-2xs text-ink-faint">{hint}</p> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* What to revise — the most actionable block, so it sits highest      */
/* ------------------------------------------------------------------ */

function ReviseBlock({
  revise,
  hasTopics,
}: {
  revise: MyTopicRow[];
  hasTopics: boolean;
}) {
  return (
    <Card>
      <CardHeader
        title="What to revise next"
        description={`Your weakest topics that you have answered enough questions on (at least ${TOPIC_ADVICE_MIN}) for the number to mean something. Start at the top.`}
      />
      {revise.length === 0 ? (
        <CardBody className="flex items-start gap-2">
          <Sparkles className="size-4 shrink-0 text-answered" />
          <p className="text-xs text-ink-muted">
            {hasTopics
              ? "No topic stands out as weak yet — you have been steady across everything you have answered enough of. Keep going, and this list will point somewhere specific if a gap opens up."
              : "There are no topic results yet. They appear once you have answered a few questions in a marked paper."}
          </p>
        </CardBody>
      ) : (
        <CardBody className="flex flex-col gap-2.5">
          {revise.map((t, i) => (
            <div
              key={t.topic_id}
              className="flex items-center gap-3 rounded-md border border-border bg-surface-sunken px-3 py-2.5"
            >
              <span className="tabular flex size-6 shrink-0 items-center justify-center rounded-sm bg-accent-soft text-2xs font-semibold text-accent">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">
                  {t.topic_name}
                </p>
                <p className="text-2xs text-ink-faint">
                  {t.subject_name} · about{" "}
                  <span className="tabular">
                    {Math.round(t.mean_time_ms / 1000)}
                  </span>
                  s per question
                </p>
              </div>
              <div className="w-28 shrink-0">
                <AccuracyBar accuracy={t.accuracy} />
              </div>
              {/* Always the denominator alongside the percentage: "0%" over one
                  question is noise, and a student should be able to see that. */}
              <span className="tabular w-24 shrink-0 text-right text-2xs text-ink-muted">
                {t.correct} of {t.answered} correct
              </span>
            </div>
          ))}
        </CardBody>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Score over time                                                     */
/* ------------------------------------------------------------------ */

function ScoreOverTime({ attempts }: { attempts: MyAttemptRow[] }) {
  const chart = buildChart(attempts);
  const { points } = chart;
  // With many sittings the date labels would collide, so thin them out rather
  // than overlap them into an unreadable smear.
  const labelEvery = points.length > 7 ? 2 : 1;

  return (
    <Card>
      <CardHeader
        title="Score over time"
        description="Percentages, not raw marks — papers are worth different totals, so raw marks would compare two different things."
      />
      <CardBody className="flex flex-col gap-3">
        <p className="text-xs text-ink-muted">{trendCaption(attempts)}</p>

        <div className="overflow-x-auto">
          <svg
            viewBox={`0 0 ${CHART.width} ${CHART.height}`}
            className="h-56 w-full min-w-[520px]"
            role="img"
            aria-label={`Your score as a percentage across ${points.length} ${
              points.length === 1 ? "sitting" : "sittings"
            }, oldest on the left. ${points
              .map(
                (p) =>
                  `${p.attempt.exam_title}, ${formatDate(
                    p.attempt.submitted_at,
                  )}: ${Math.round(p.attempt.percent)} percent${
                    p.isRetake ? `, sitting number ${p.attempt.attempt_no}` : ""
                  }.`,
              )
              .join(" ")}`}
          >
            {chart.grid.map((g) => (
              <g key={g.label}>
                <line
                  x1={chart.plotLeft}
                  x2={chart.plotRight}
                  y1={g.y}
                  y2={g.y}
                  className="stroke-border"
                  strokeWidth={1}
                />
                <text
                  x={chart.plotLeft - 6}
                  y={g.y + 3}
                  textAnchor="end"
                  className="fill-ink-faint text-[10px]"
                >
                  {g.label}
                </text>
              </g>
            ))}

            {/* Connectors. A segment joining two sittings of the SAME paper is
                dashed, because it is a repeat rather than a step forward in
                time — the dash carries that meaning without relying on colour. */}
            {points.slice(1).map((p, i) => {
              // `slice(1)` shifts the index, so `points[i]` is the point
              // BEFORE `p`; the guard is only here to satisfy the checker's
              // index-access strictness, and cannot fire.
              const prev = points[i];
              if (!prev) return null;
              return (
                <line
                  key={`seg-${p.attempt.attempt_id}`}
                  x1={prev.x}
                  y1={prev.y}
                  x2={p.x}
                  y2={p.y}
                  className={cn(
                    "stroke-accent",
                    p.sameExamAsPrevious && "opacity-60",
                  )}
                  strokeWidth={2}
                  strokeDasharray={p.sameExamAsPrevious ? "4 4" : undefined}
                >
                  <title>
                    {p.sameExamAsPrevious
                      ? `Same paper sat again: ${prev.attempt.exam_title}`
                      : `${prev.attempt.exam_title} → ${p.attempt.exam_title}`}
                  </title>
                </line>
              );
            })}

            {points.map((p, i) => (
              <Marker
                key={p.attempt.attempt_id}
                point={p}
                index={i + 1}
                showLabel={i % labelEvery === 0 || i === points.length - 1}
                labelY={chart.plotBottom + 18}
                numberY={chart.plotBottom + 34}
              />
            ))}
          </svg>
        </div>

        <ChartLegend
          anyRetake={points.some((p) => p.isRetake)}
          anyCohort={points.some((p) => p.cohortY !== null)}
        />
      </CardBody>
    </Card>
  );
}

function Marker({
  point,
  index,
  showLabel,
  labelY,
  numberY,
}: {
  point: ProgressPoint;
  index: number;
  showLabel: boolean;
  labelY: number;
  numberY: number;
}) {
  const { attempt } = point;
  const cohort =
    attempt.cohort_mean_percent === null
      ? "Class average not shown yet for this paper."
      : `Class average ${Math.round(attempt.cohort_mean_percent)}%.`;
  const title = `${attempt.exam_title} — ${Math.round(attempt.percent)}% on ${formatDate(
    attempt.submitted_at,
  )}${attempt.attempt_no > 1 ? ` (sitting ${attempt.attempt_no})` : ""}. ${cohort}`;

  return (
    <g>
      {/* The class mean for the same paper, as a short rule rather than a
          second dot, so it never competes with the student's own score. */}
      {point.cohortY !== null ? (
        <line
          x1={point.x - 9}
          x2={point.x + 9}
          y1={point.cohortY}
          y2={point.cohortY}
          className="stroke-ink-faint"
          strokeWidth={2}
        >
          <title>{`Class average on ${attempt.exam_title}: ${Math.round(
            attempt.cohort_mean_percent ?? 0,
          )}% (${attempt.cohort_size} students).`}</title>
        </line>
      ) : null}

      {/* Retakes get a square, first sittings a circle: shape, not hue, is what
          distinguishes them, so the difference survives a colourblind reader
          and a black-and-white print. */}
      {point.isRetake ? (
        <rect
          x={point.x - 4.5}
          y={point.y - 4.5}
          width={9}
          height={9}
          className="fill-surface stroke-accent"
          strokeWidth={2}
        >
          <title>{title}</title>
        </rect>
      ) : (
        <circle
          cx={point.x}
          cy={point.y}
          r={4.5}
          className="fill-accent stroke-surface"
          strokeWidth={2}
        >
          <title>{title}</title>
        </circle>
      )}

      {showLabel ? (
        <text
          x={point.x}
          y={labelY}
          textAnchor="middle"
          className="fill-ink-faint text-[10px]"
        >
          {formatDate(attempt.submitted_at)}
        </text>
      ) : null}
      {/* The numeral ties this point to the numbered row in "Every sitting"
          below, which is where the full exam title fits without collisions. */}
      <text
        x={point.x}
        y={numberY}
        textAnchor="middle"
        className="fill-ink-muted text-[10px] font-semibold"
      >
        {index}
      </text>
    </g>
  );
}

function ChartLegend({
  anyRetake,
  anyCohort,
}: {
  anyRetake: boolean;
  anyCohort: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-2xs text-ink-muted">
      <span className="inline-flex items-center gap-1.5">
        <svg width="16" height="10" aria-hidden="true">
          <circle cx="8" cy="5" r="4" className="fill-accent" />
        </svg>
        Your score
      </span>
      {anyRetake ? (
        <span className="inline-flex items-center gap-1.5">
          <svg width="16" height="10" aria-hidden="true">
            <rect
              x="3.5"
              y="0.5"
              width="9"
              height="9"
              className="fill-surface stroke-accent"
              strokeWidth={2}
            />
          </svg>
          A repeat of the same paper (dashed link) — not a step forward in time
        </span>
      ) : null}
      {anyCohort ? (
        <span className="inline-flex items-center gap-1.5">
          <svg width="18" height="10" aria-hidden="true">
            <line
              x1="1"
              x2="17"
              y1="5"
              y2="5"
              className="stroke-ink-faint"
              strokeWidth={2}
            />
          </svg>
          Class average on that paper
        </span>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Every topic                                                         */
/* ------------------------------------------------------------------ */

function AllTopics({ topics }: { topics: MyTopicRow[] }) {
  if (topics.length === 0) return null;
  const sorted = sortTopics(topics);
  const thinCount = sorted.filter(isTooThinToAct).length;

  return (
    <Card>
      <CardHeader
        title="Every topic you have answered"
        description={`Weakest first. Topics with fewer than ${TOPIC_ADVICE_MIN} answered questions are marked — there is not enough there yet to revise from.`}
      />
      <CardBody className="flex flex-col gap-1.5">
        {sorted.map((t) => {
          const thin = isTooThinToAct(t);
          return (
            <div
              key={t.topic_id}
              className="flex items-center gap-3 rounded-sm px-2 py-1.5 odd:bg-surface-sunken"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-ink">
                  {t.topic_name}
                </p>
                <p className="text-2xs text-ink-faint">{t.subject_name}</p>
              </div>
              {thin ? (
                <Badge tone="neutral">Too few to judge</Badge>
              ) : (
                <div className="w-24 shrink-0">
                  <AccuracyBar accuracy={t.accuracy} />
                </div>
              )}
              <span
                className={cn(
                  "tabular w-24 shrink-0 text-right text-2xs",
                  thin ? "text-ink-faint" : "text-ink-muted",
                )}
              >
                {t.correct} of {t.answered} correct
              </span>
            </div>
          );
        })}
        {thinCount > 0 ? (
          <p className="mt-1 text-2xs text-ink-faint">
            {thinCount === 1 ? "One topic is" : `${thinCount} topics are`}{" "}
            marked “too few to judge”: a percentage from one or two questions
            says more about which questions came up than about you.
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Time management                                                     */
/* ------------------------------------------------------------------ */

function TimeManagement({ attempts }: { attempts: MyAttemptRow[] }) {
  const rows = attempts
    .map(timeUse)
    .sort((a, b) => b.attempt.submitted_at - a.attempt.submitted_at);
  const measured = rows.filter((r) => r.ratio !== null);

  return (
    <Card>
      <CardHeader
        title="How you use the time"
        description="Against the time you were actually allowed on the day, which already includes any extra time granted to you."
      />
      <CardBody className="flex flex-col gap-3">
        {measured.length > 0 ? (
          <p className="text-xs text-ink-muted">
            <Clock className="mr-1 inline size-3.5 align-[-2px] text-ink-faint" />
            {summariseTime(measured.map((r) => r.ratio ?? 0))}
          </p>
        ) : null}

        {rows.map(({ attempt, ratio, note }) => (
          <div key={attempt.attempt_id}>
            <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
              <span className="min-w-0 truncate font-medium text-ink">
                {attempt.exam_title}
                {attempt.attempt_no > 1 ? (
                  <span className="ml-1.5 text-2xs font-normal text-ink-faint">
                    sitting <span className="tabular">{attempt.attempt_no}</span>
                  </span>
                ) : null}
              </span>
              <span className="tabular shrink-0 text-ink-muted">
                {attempt.time_taken_ms === null
                  ? "—"
                  : formatDuration(attempt.time_taken_ms)}{" "}
                <span className="text-ink-faint">
                  of {formatDuration(attempt.time_allowed_ms)} allowed
                </span>
              </span>
            </div>
            <div
              className="h-2 w-full overflow-hidden rounded-xs bg-surface-sunken"
              role="img"
              aria-label={`${attempt.exam_title}: ${
                ratio === null
                  ? "no timing recorded"
                  : `${Math.round(ratio * 100)} percent of the allowed time used`
              }`}
            >
              <div
                className={cn(
                  "h-full rounded-xs",
                  ratio === null
                    ? "bg-border-strong"
                    : ratio >= 0.97
                      ? "bg-review"
                      : "bg-accent",
                )}
                style={{
                  width: `${Math.max(0, Math.min(1, ratio ?? 0)) * 100}%`,
                }}
              />
            </div>
            <p className="mt-0.5 text-2xs text-ink-faint">{note}</p>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}

/** One honest sentence about pace, based on the average share of the allowance
 *  used — a student wants to know "am I running out of time?", not a table. */
function summariseTime(ratios: number[]): string {
  const mean = ratios.reduce((sum, r) => sum + r, 0) / ratios.length;
  if (mean >= 0.95) {
    return "You are typically right up against the clock. Practising a fixed time per question is usually worth more marks than any extra revision.";
  }
  if (mean >= 0.75) {
    return "You typically use most of the time but finish. That is a reasonable place to be — the risk is a long question early on.";
  }
  if (mean >= 0.5) {
    return "You usually finish with time in hand. Spending some of it re-checking your answers is close to free marks.";
  }
  return "You are finishing well inside the time. If your accuracy is not where you want it, slowing down is the cheapest fix available to you.";
}

/* ------------------------------------------------------------------ */
/* Every sitting                                                       */
/* ------------------------------------------------------------------ */

function AttemptList({ attempts }: { attempts: MyAttemptRow[] }) {
  // Numbered oldest-first to match the chart, then shown newest-first, which is
  // the order a student scans for "how did the last one go?".
  const oldestFirst = [...attempts].sort(
    (a, b) => a.submitted_at - b.submitted_at,
  );
  const numbered = oldestFirst.map((attempt, i) => ({ attempt, index: i + 1 }));
  const rows = [...numbered].reverse();

  return (
    <Card>
      <CardHeader
        title="Every sitting"
        description="The numbers match the points on the chart above."
      />
      <CardBody className="flex flex-col gap-2">
        {rows.map(({ attempt, index }) => (
          <div
            key={attempt.attempt_id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-border bg-surface-sunken px-3 py-2.5"
          >
            <span className="tabular flex size-6 shrink-0 items-center justify-center rounded-sm border border-border-strong bg-surface text-2xs font-semibold text-ink-muted">
              {index}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink">
                {attempt.exam_title}
                {attempt.attempt_no > 1 ? (
                  <span className="ml-2 align-middle">
                    <Badge tone="review">
                      Sitting {attempt.attempt_no}
                    </Badge>
                  </span>
                ) : null}
              </p>
              <p className="text-2xs text-ink-faint">
                {attempt.subject_name} · {formatDateTime(attempt.submitted_at)}
              </p>
            </div>

            <div className="text-right">
              <p className="tabular text-sm font-semibold text-ink">
                {Math.round(attempt.percent)}%
              </p>
              <p className="tabular text-2xs text-ink-faint">
                {attempt.score} of {attempt.max_score} marks
              </p>
            </div>

            <div className="w-48 shrink-0 text-right">
              <CohortNote attempt={attempt} />
            </div>

            {/* `/student/exam/:attemptId/result` is the student's own result
                page (see App.tsx) and is ownership-gated server-side, so every
                row here is reachable — these are the student's own attempts. */}
            <Link
              to={`/student/exam/${attempt.attempt_id}/result`}
              className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-accent hover:underline"
            >
              <BarChart3 className="size-3.5" />
              Result
            </Link>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}

/**
 * The class figure, framed as context rather than a placing: an anonymous mean
 * and how far off it you were, never a rank. When the API withholds it there is
 * deliberately no gap shown — saying "not enough people yet" is honest, whereas
 * a comparison against two classmates would not be.
 */
function CohortNote({ attempt }: { attempt: MyAttemptRow }) {
  if (attempt.cohort_mean_percent === null) {
    return (
      <p className="text-2xs text-ink-faint">
        Class average appears once at least{" "}
        <span className="tabular">{COHORT_MEAN_MIN}</span> students have sat
        this paper.
      </p>
    );
  }
  const diff = attempt.percent - attempt.cohort_mean_percent;
  const rounded = Math.round(Math.abs(diff));
  return (
    <p className="text-2xs text-ink-muted">
      <Target className="mr-1 inline size-3 align-[-1px] text-ink-faint" />
      Class average{" "}
      <span className="tabular font-medium">
        {Math.round(attempt.cohort_mean_percent)}%
      </span>
      <span className="block text-ink-faint">
        {rounded === 0
          ? "about the same as you"
          : `${rounded} points ${diff > 0 ? "below" : "above"} your score`}{" "}
        · <span className="tabular">{attempt.cohort_size}</span> students
      </span>
    </p>
  );
}

/* ------------------------------------------------------------------ */
/* Shared bits                                                         */
/* ------------------------------------------------------------------ */

/** Same honest-but-not-harsh scale the result page uses, so a topic that looks
 *  amber there does not look red here. */
function AccuracyBar({ accuracy }: { accuracy: number }) {
  const pct = Math.max(0, Math.min(1, accuracy)) * 100;
  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-xs bg-surface"
      role="img"
      aria-label={`${Math.round(pct)} percent correct`}
    >
      <div
        className={cn(
          "h-full rounded-xs",
          accuracy >= 0.6
            ? "bg-answered"
            : accuracy >= 0.35
              ? "bg-review"
              : "bg-violation",
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
